import { Fragment, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { AmountPicker } from '@understory-io/leaf/amount-picker';
import { Divider } from '@understory-io/leaf/divider';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { InfoPopover } from '~/ui/info-popover';
import { FormattedPrice, type FormattedPriceInput } from '~/ui/formatted';
import { perBookingMax, PerBookingMaxHint } from './per-booking-max';

/**
 * Flattened ticket picker — the successor to `TicketsField` (which launches a
 * popover/drawer). This renders the variants inline instead: each variant is a
 * card with a raised (`surface.raised`) top zone holding the variant + its
 * stepper, and — when it has add-ons — a recessed (`surface.base`) bottom zone
 * holding the add-on rows, joined by a single seam line.
 *
 * It's a deliberate standalone copy rather than a variant of `TicketsField`, so
 * the existing checkout flows that still use the popover stay untouched while
 * flows migrate to this one (gift cards first). Fully controlled: it emits the
 * full next quantities map and the caller owns the state and pricing.
 */

export type TicketSelectorAddOn = {
  id: string;
  name: string;
  /** Optional free-text description, surfaced via an info popover next to the
   *  add-on name. Null/empty hides the info affordance. */
  description?: string | null;
  /** Price breakdown for display. Null when no price is configured. */
  price: FormattedPriceInput | null;
};

export type TicketSelectorVariant = {
  id: string;
  name: string;
  /** Optional free-text description, surfaced via an info popover next to the
   *  variant name. Null/empty hides the info affordance. */
  description?: string | null;
  /** Price breakdown for display. Null when no price is configured. */
  price: FormattedPriceInput | null;
  /** Optional add-ons, selectable only while the variant quantity is > 0. */
  addOns?: TicketSelectorAddOn[];
};

/** Next quantities for a variant change; resets the variant's add-ons to 0 when
 *  it drops to 0. */
function applyVariantChange(
  quantities: Record<string, number>,
  variant: TicketSelectorVariant,
  quantity: number
): Record<string, number> {
  const next = { ...quantities, [variant.id]: quantity };
  if (quantity === 0) {
    for (const addOn of variant.addOns ?? []) next[addOn.id] = 0;
  }
  return next;
}

/** Next quantities for an add-on change. */
function applyAddOnChange(
  quantities: Record<string, number>,
  addOnId: string,
  quantity: number
): Record<string, number> {
  return { ...quantities, [addOnId]: quantity };
}

type TicketsSelectorProps = {
  /** Variants the visitor can pick quantities for. */
  variants: TicketSelectorVariant[];
  /** Current quantities keyed by variant/add-on id. */
  quantities: Record<string, number>;
  /** Emits the full next quantities map (handles add-on resets atomically). */
  onQuantitiesChange: (next: Record<string, number>) => void;
  /** Step for seat variants (the experience's multiple-of rule). Add-ons always
   *  step by 1. Defaults to 1. */
  seatStep?: number;
  /** The experience's max tickets per booking. Caps the seat steppers and
   *  shows a hint once reached. Omitted or 0 means no max. */
  maxSeats?: number;
  /** Validation/error message rendered beneath the list, styled critical. */
  errorMessage?: ReactNode;
};

export type { TicketsSelectorProps };

export function TicketsSelector({
  variants,
  quantities,
  onQuantitiesChange,
  seatStep = 1,
  maxSeats,
  errorMessage,
}: TicketsSelectorProps) {
  const intl = useIntl();
  const bookingMax = perBookingMax(maxSeats, variants, quantities);
  // An error already says what to fix; the hint would only repeat it.
  const hintMax =
    bookingMax.reached && !errorMessage ? bookingMax.max : undefined;

  return (
    <Stack gap="medium">
      {variants.map((variant) => {
        const labelId = `tickets-selector-variant-${variant.id}`;
        const variantQty = quantities[variant.id] ?? 0;
        const addOns = variant.addOns ?? [];
        const hasAddOns = addOns.length > 0;
        return (
          <div key={variant.id} className={cardStyle}>
            <div className={hasAddOns ? topZoneWithSeamStyle : topZoneStyle}>
              <Stack
                direction="row"
                align="center"
                justify="space-between"
                gap="medium"
              >
                <Stack direction="column" gap="micro">
                  <Stack direction="row" align="center" gap="xsmall">
                    <Text id={labelId} style="body-small-bold">
                      {variant.name}
                    </Text>
                    {variant.description && (
                      <InfoPopover
                        openOnHover
                        label={intl.formatMessage(
                          {
                            defaultMessage: 'Show description for {name}',
                            description:
                              'Accessible label for the ticket variant description button',
                          },
                          { name: variant.name }
                        )}
                      >
                        {variant.description}
                      </InfoPopover>
                    )}
                  </Stack>
                  {variant.price !== null && (
                    <Text style="body-small-regular" color="tertiary">
                      <FormattedPrice price={variant.price} />
                    </Text>
                  )}
                </Stack>
                <AmountPicker
                  labelledBy={labelId}
                  min={0}
                  max={bookingMax.variantMax(variantQty)}
                  step={seatStep}
                  value={variantQty}
                  onChange={(value) =>
                    onQuantitiesChange(
                      applyVariantChange(quantities, variant, value ?? 0)
                    )
                  }
                />
              </Stack>
            </div>

            {hasAddOns && (
              <div className={addOnZoneStyle}>
                <Stack gap="xsmall">
                  <Text style="body-small-bold">
                    {intl.formatMessage({
                      defaultMessage: 'Add-ons',
                      description:
                        'Heading above a ticket variant’s add-on rows in the ticket selector',
                    })}
                  </Text>
                  <Stack gap="small">
                    {addOns.map((addOn, index) => {
                      const addOnLabelId = `tickets-selector-addon-${addOn.id}`;
                      return (
                        <Fragment key={addOn.id}>
                          {index > 0 && <Divider />}
                          <Stack
                            direction="row"
                            align="center"
                            justify="space-between"
                            gap="large"
                          >
                            <Stack direction="column" gap="micro">
                              <Stack direction="row" align="center" gap="xsmall">
                                <Text
                                  id={addOnLabelId}
                                  style="body-small-regular"
                                >
                                  {addOn.name}
                                </Text>
                                {addOn.description && (
                                  <InfoPopover
                                    openOnHover
                                    label={intl.formatMessage(
                                      {
                                        defaultMessage:
                                          'Show description for {name}',
                                        description:
                                          'Accessible label for the ticket variant description button',
                                      },
                                      { name: addOn.name }
                                    )}
                                  >
                                    {addOn.description}
                                  </InfoPopover>
                                )}
                              </Stack>
                              {addOn.price !== null && (
                                <Text
                                  style="body-small-regular"
                                  color="secondary"
                                >
                                  <FormattedPrice price={addOn.price} />
                                </Text>
                              )}
                            </Stack>
                            <AmountPicker
                              labelledBy={addOnLabelId}
                              min={0}
                              disabled={variantQty === 0}
                              value={quantities[addOn.id] ?? 0}
                              onChange={(value) =>
                                onQuantitiesChange(
                                  applyAddOnChange(
                                    quantities,
                                    addOn.id,
                                    value ?? 0
                                  )
                                )
                              }
                            />
                          </Stack>
                        </Fragment>
                      );
                    })}
                  </Stack>
                </Stack>
              </div>
            )}
          </div>
        );
      })}

      {hintMax !== undefined && <PerBookingMaxHint max={hintMax} />}

      {errorMessage && (
        <Text
          role="alert"
          style="body-small-regular"
          className={css({ color: 'status.negative.bold' })}
        >
          {errorMessage}
        </Text>
      )}
    </Stack>
  );
}

// One card per variant: a continuous neutral outline + rounded corners, with
// the square inner-zone corners clipped to it.
const cardStyle = css({
  borderRadius: 'medium',
  border: 'small',
  borderColor: 'border.neutral',
  overflow: 'hidden',
});

// Variant zone (raised). When add-ons follow, its bottom border is the seam
// between the two zones.
const topZoneStyle = css({
  background: 'surface.raised',
  padding: 'medium',
});

const topZoneWithSeamStyle = css({
  background: 'surface.raised',
  padding: 'medium',
  borderBottom: 'small',
  borderBottomColor: 'border.neutral',
});

// Add-ons zone (recessed), full-width below the seam.
const addOnZoneStyle = css({
  background: 'surface.base',
  padding: 'medium',
});
